import { z } from 'zod';
import {
  DEFAULT_MANAGER_OPERATING_SYSTEM_DATE_RANGE,
  MANAGER_OPERATING_SYSTEM_ALL_FILTER_VALUE,
  MANAGER_OPERATING_SYSTEM_DATE_RANGE_VALUES,
} from '@act/shared/constants';

const queryEmptyToUndefined = (val: unknown): unknown =>
  val === '' || val === undefined || val === null ? undefined : val;

export { MANAGER_OPERATING_SYSTEM_DATE_RANGE_VALUES } from '@act/shared/constants';

/**
 * Query string for `GET /guides/manager-operating-system/new-engagement-metrics`.
 */
export const ManagerOperatingSystemNewEngagementMetricsQuerySchema = z.object({
  dateRange: z.preprocess(
    queryEmptyToUndefined,
    z
      .enum(MANAGER_OPERATING_SYSTEM_DATE_RANGE_VALUES)
      .default(DEFAULT_MANAGER_OPERATING_SYSTEM_DATE_RANGE)
  ),
  team: z.preprocess(
    queryEmptyToUndefined,
    z
      .union([
        z.literal(MANAGER_OPERATING_SYSTEM_ALL_FILTER_VALUE),
        z.coerce.number().int().positive(),
      ])
      .default(MANAGER_OPERATING_SYSTEM_ALL_FILTER_VALUE)
  ),
  client: z.preprocess(
    queryEmptyToUndefined,
    z
      .union([
        z.literal(MANAGER_OPERATING_SYSTEM_ALL_FILTER_VALUE),
        z.coerce.number().int().positive(),
      ])
      .default(MANAGER_OPERATING_SYSTEM_ALL_FILTER_VALUE)
  ),
});

/**
 * Validated query for {@link ManagerOperatingSystemNewEngagementMetricsQuerySchema}.
 */
export type ManagerOperatingSystemNewEngagementMetricsQueryDto = z.infer<
  typeof ManagerOperatingSystemNewEngagementMetricsQuerySchema
>;

/**
 * Actual/target metric for the selected range, plus month-to-date pace.
 *
 * `actual` and `target` describe only the selected range, while `isOn`
 * answers the month-to-date question: has the guide kept pace with the
 * business days of the month that have elapsed? A slow single day therefore
 * does not flip a guide Off while the month is still on track.
 */
export interface ManagerOperatingSystemActivityMetricDto {
  actual: number;
  target: number;
  cumulativeMonthly: number;
  cumulativeExpectedToDate: number;
  benchmarkMonthly: number;
  isOn: boolean;
}

/**
 * Candidate supply compared with candidate demand.
 */
export interface ManagerOperatingSystemCandidateAvailabilityMetricDto {
  available: number;
  needed: number;
  isOn: boolean;
}

/**
 * Completion metric with counts and a benchmarked completion rate.
 */
export interface ManagerOperatingSystemCompletionMetricDto {
  completed: number;
  needed: number;
  percentComplete: number;
  benchmark: number;
  isOn: boolean;
}

/**
 * Rate metric compared with its warehouse benchmark.
 */
export interface ManagerOperatingSystemRateMetricDto {
  value: number;
  benchmark: number;
  isOn: boolean;
}

/**
 * New-engagement metric whose `target` is the business-day-prorated
 * `guide_targets` value rather than a warehouse day target.
 */
export type ManagerOperatingSystemNewEngagementMetricDto =
  ManagerOperatingSystemActivityMetricDto;

/**
 * Grouped MOS metrics for one guide.
 */
export interface ManagerOperatingSystemGuideMetricsDto {
  /** Last day with warehouse data in the window (`YYYY-MM-DD`), or null. */
  asOfDay: string | null;
  candidateAvailability: ManagerOperatingSystemCandidateAvailabilityMetricDto;
  uniqueMembersOutreached: ManagerOperatingSystemActivityMetricDto;
  calls: ManagerOperatingSystemActivityMetricDto;
  texts: ManagerOperatingSystemActivityMetricDto;
  homeVisits: ManagerOperatingSystemActivityMetricDto;
  identityVerification: ManagerOperatingSystemCompletionMetricDto;
  consents: ManagerOperatingSystemCompletionMetricDto;
  interactionRate: ManagerOperatingSystemRateMetricDto;
  engagementRate: ManagerOperatingSystemRateMetricDto;
  newEngagement: ManagerOperatingSystemNewEngagementMetricDto;
}

/**
 * New-engagement totals and grouped lane metrics for one guide.
 */
export interface ManagerOperatingSystemGuideNewEngagementDto {
  guideId: string;
  /** Display name from the same roster query that scoped these metrics. */
  name: string;
  actual: number;
  target: number;
  metrics: ManagerOperatingSystemGuideMetricsDto;
}

/**
 * Team-level new-engagement actual vs prorated `guides.guide_targets` totals.
 */
export interface ManagerOperatingSystemTeamNewEngagementDto {
  actual: number;
  target: number;
}

/**
 * One customer the acting manager can filter to, from the same roster scope
 * that produced these metrics.
 */
export interface ManagerOperatingSystemClientChoiceDto {
  id: number;
  name: string;
}

/**
 * MOS new-engagement actual vs target for the selected date range, team, and
 * customer.
 */
export interface ManagerOperatingSystemNewEngagementMetricsDto {
  /** Epoch millis of the newest `loaded_at` in the aggregated window, or now when empty. */
  dataAsOf: number;
  /**
   * Customers on the selected team(s), unaffected by the customer filter so
   * the picker keeps every option after a selection.
   */
  clients: ManagerOperatingSystemClientChoiceDto[];
  team: ManagerOperatingSystemTeamNewEngagementDto;
  guides: ManagerOperatingSystemGuideNewEngagementDto[];
}
